import { useMemo, useState } from "react";
import { MenuItem } from "./menuItem";
import { SubMenu } from './SubMenu';
import { 
    UserPagePermisions, 
    uppData, 
    AdminList, 
    PermissionsObject, 
    Permissions, 
    UserData  
} from "./types/globalTypes";
import { Link, NavLink } from "react-router-dom";


//uppMap is for UserType 5 and 7, this is all it needs
const uppMap: uppData = {
    Administrator: {
        url: "/Credentials/Subcontractor/SubcontractorAccess",
        icon: "admin_panel_settings",
        text: "Admin",
    },
    Dispatch: {
        url: "/Credentials/Subcontractor/SubcontractorDispatch",
        icon: "airport_shuttle",
        text: "Dispatch"
    }
}

// --------------------------------------------------------------------------------
// This area is where we will put our todos and bugs needed to be fixed for this project
// for when we leave for the day.
// --------------------------------------------------------------------------------
// TODO:
// 1. Make the sidemenu not appear if user has not clicked the hamburger menu
// 2. Make the sidemenu appear (near clicked) if user has clicked the hamburger menu
// 3. Make the sidemenu disappear if user has clicked the hamburger menu again
// 4. Make the sidemenu disappear if user has clicked outside of the sidemenu
// 5. Fix the URL for all buttons. (Not redirecting you to the correct page)
// 6. Get the styling matching with old website 
// --------------------------------------------------------------------------------
// COMPLETED:
// 1. Make the sidemenu appear all buttons needed with the permission set
// 2. Make nested submenus appear (indefinitely)
// 3. Moved the types to a separate file
// Added by: Alex R.
// Date: 11/9/2023


const reportConfiguratorPath = "/Reports/ReportConfiguration";
const reportPreviewPath = `${reportConfiguratorPath}/Preview`;

const normalizeBaseUrl = (url?: string | null) => {
    const trimmedUrl = url?.trim().replace(/\/+$/, "") ?? "";

    if (!trimmedUrl) return "";
    if (/^https?:\/\//i.test(trimmedUrl)) return trimmedUrl;

    return `${window.location.protocol}//${trimmedUrl}`;
}

const buildUrl = (baseUrl?: string | null, path?: string | null) => {
    const normalizedBaseUrl = normalizeBaseUrl(baseUrl);
    const normalizedPath = path?.replace(/^\/+/, "") ?? "";

    if (!normalizedBaseUrl) return normalizedPath ? `/${normalizedPath}` : "";
    return normalizedPath ? `${normalizedBaseUrl}/${normalizedPath}` : normalizedBaseUrl;
}

const isLocalOrigin = (origin: string) => /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?$/i.test(origin);


const Menu = ({ permissions, parentCollapsed, userType, ctcConnectUIURL, net6APIURL, webAppURL }) => {

    const userData: UserData = useMemo(() => {
        const temp = {
            userType: userType,
            userPermissions: permissions,
            adminList: JSON.parse(window.localStorage.getItem("adminList") ?? "{}"),
            userPagePermissions: JSON.parse(window.localStorage.getItem("userPagePermissions") ?? "{}")
        }
        return temp 
    }, [permissions]);
    const currentOrigin = window.location.origin;
    const ctcConnectUIBaseUrl = normalizeBaseUrl(ctcConnectUIURL);
    const newApiBaseUrl = isLocalOrigin(currentOrigin) ? currentOrigin : normalizeBaseUrl(net6APIURL);
    const oldPermissionBaseUrl = normalizeBaseUrl(webAppURL);
    const [ssrsReportConfig, setSsrsReportConfig] = useState({});
    const [reportConfigurator, setReportConfigurator] = useState({});
    const [appversionConfig, setAppVersionConfig] = useState({});
    const previewPermission = (Object.values(permissions ?? {}) as PermissionsObject[]).find(p =>
        /(?:^|\/)reports\/reportconfiguration\/preview\/?$/i.test(p.url ?? ""))
        ?? (Object.values(permissions ?? {}) as PermissionsObject[]).find(p =>
            /(?:^|\/)reports\/reportconfiguration\/?$/i.test(p.url ?? ""));
    const reportPreviewUrl = previewPermission ? reportPreviewPath : "";
    const sidebarMenu: uppData = useMemo(() => {
        const permissions = userData.userPermissions ?? {}
        const upMap: uppData = {};
        const busPassAssignmentPath = "/Reservation/Reservation/BusPassAssignment";
        const getPermissionBaseUrl = (permissionBaseUrl?: string | null) => {
            const permissionBase = permissionBaseUrl?.trim().toLowerCase();

            if (permissionBase === "ctc-connect-ui") return ctcConnectUIBaseUrl;
            if (permissionBase === "net6-api") return newApiBaseUrl;

            return oldPermissionBaseUrl;
        };
        const buildPermissionUrl = (element: PermissionsObject, path = element.url) => buildUrl(getPermissionBaseUrl(element?.baseUrl), path);
        const busPassAssignmentUrl = buildUrl(newApiBaseUrl, busPassAssignmentPath);

        for (const key in permissions) {
            const element: PermissionsObject = permissions[key];
            let navUrl = buildPermissionUrl(element);
            let isSsrsReport = false;
            let isReportConfigurator = false;
            let isAppVersion = false;


            const elementUrlLower = element?.url?.toLowerCase() ?? "";
            // The nested preview route has its own menu entry; never rewrite it to the configuration editor.
            if (elementUrlLower.includes("reports/reportconfiguration/preview")) continue;
            const isAppVersionsUrl =
                elementUrlLower.includes("app/versions") ||
                elementUrlLower.includes("app/version") ||
                elementUrlLower.includes("appversions");

            if (isAppVersionsUrl) {
                navUrl = buildPermissionUrl(element);
                const appVersionUrlWithPermission = element.permissionId
                    ? `${navUrl}${navUrl.includes("?") ? "&" : "?"}permissionId=${element.permissionId}`
                    : navUrl;
                setAppVersionConfig({
                    navUrl: appVersionUrlWithPermission,
                    text: element.displayName,
                    permissionId: element.permissionId
                });
                isAppVersion = true;
            }
            if (element?.url?.toLowerCase().includes("reports/reportconfiguration")) {
                navUrl = buildPermissionUrl(element, "Reports/ReportConfiguration");
                setReportConfigurator({
                    navUrl: navUrl,
                    text: element.displayName,
                });
                isReportConfigurator = true;
            }
            if (element?.url?.toLowerCase().includes("ssrs/reports")) {
                navUrl = buildPermissionUrl(element);
                setSsrsReportConfig({
                    navUrl: navUrl,
                    text: element.displayName,
                });
                isSsrsReport = true;
            }
            if (!(isSsrsReport || isAppVersion || isReportConfigurator)) {
                if (!upMap[element.area]) {
                    upMap[element.area] = {
                        icon: element.icon,
                        url: `#${element.area}`,
                        text: element.area,
                        permissionId: element.permissionId,
                    };
                    if (!element.subMenu) {
                        upMap[element.area].subMenu = [{ url: navUrl, text: element.displayName, permissionId: element.permissionId }];
                    } else {
                        upMap[element.area].subMenu = [{
                            text: element.subMenu,
                            subMenu: [{ url: navUrl, text: element.displayName, permissionId: element.permissionId }]
                        }];
                    }
                } else {
                    if (element.subMenu) {
                        const index: number = upMap[element.area].subMenu?.findIndex(x => x.text == element.subMenu) ?? -1;

                        if (index != -1) {
                            upMap[element.area].subMenu[index]?.subMenu?.push({
                                url: navUrl,
                                text: element.displayName,
                                permissionId: element.permissionId,
                            });
                        } else {
                            upMap[element.area].subMenu?.push({
                                text: element.subMenu,
                                subMenu: [{ url: navUrl, text: element.displayName, permissionId: element.permissionId }]
                            });
                        }
                    } else {
                        upMap[element.area].subMenu?.push({
                            text: element.displayName,
                            url: navUrl,
                            permissionId: element.permissionId,
                        });
                    }
                }
            }
        }

        const reservationMenuKey = Object.keys(upMap).find(x => x?.toLowerCase() === "reservation");
        if (reservationMenuKey) {
            const reservationMenu = upMap[reservationMenuKey];
            const hasBusPassAssignment = reservationMenu.subMenu?.some(x =>
                x.text === "Bus Pass Assignment"
                || x.url?.toLowerCase().includes("reservation/reservation/buspassassignment")
                || x.subMenu?.some(y => y.text === "Bus Pass Assignment" || y.url?.toLowerCase().includes("reservation/reservation/buspassassignment"))
            );

            if (!hasBusPassAssignment) {
                reservationMenu.subMenu = [
                    ...(reservationMenu.subMenu ?? []),
                    { url: busPassAssignmentUrl, text: "Bus Pass Assignment" }
                ];
            }
        }

        return upMap;

    }, [userData, ctcConnectUIBaseUrl, newApiBaseUrl, oldPermissionBaseUrl])


    //function buildUserPagePermissions(userPagePermissions: UserPagePermisions | null) {
    //    return Object.keys(uppMap).forEach((element) => {
    //        if (userPagePermissions != null) {
    //            if (userPagePermissions[element]) {
    //                return <MenuItem url={uppMap[element].url?y} icon={uppMap[element].icon} text={uppMap[element].text} />
    //            }
    //        }
    //    }
    //    );
    //}                 {/*{(userData.userType == 5 || userData.userType == 7) ?? buildUserPagePermissions(userData.userPagePermissions)}*/}


    return (
        <div className="sidebar">
            <ul id="dynMenu" className="sidebar-menu">
                <li key={"23421" } className="treeview">
                    <Link to={buildUrl(oldPermissionBaseUrl, "/Account/Dashboard")}>
                        <div className="new-icon-Container">
                            <span className="material-icons">home</span>
                        </div>
                        <span>Home</span>
                        <span className="material-icons md-14 pull-right" />
                    </Link>
                </li>

                {
                    Object.values(sidebarMenu).map((elem) =>
                        <MenuItem key={JSON.stringify(elem)} url={elem.url} icon={elem.icon ?? ""} text={elem.text ?? ""} subMenu={elem.subMenu} permissionId={elem.permissionId} parentCollapsed={parentCollapsed} />
                    )
                }
                {userType == 3 &&
                    <li key={"23422"} className="treeview">
                    <NavLink to={"/grievances"}>
                        <div className="new-icon-Container">
                            <span className="material-icons">feedback</span>
                        </div>
                        <span>Grievances</span>
                        <span className="material-icons md-14 pull-right" />
                    </NavLink>
                </li>}  
                {userType == 1 &&
                    <li key={"23422"} className="treeview">
                        <NavLink to="/complaints/search">
                            <div className="new-icon-Container">
                                <span className="material-icons">feedback</span>
                            </div>
                            <span>Complaints</span>
                            <span className="material-icons md-14 pull-right" />
                        </NavLink>
                    </li>}
                {ssrsReportConfig?.navUrl &&
                    <li key={"093281"} className="treeview">
                        <NavLink to={ssrsReportConfig?.navUrl}>
                            <div className="new-icon-Container">
                                <span className="material-icons">bar_chart</span>
                            </div>
                            <span>{ssrsReportConfig?.text}</span>
                        </NavLink>
                    </li>}
                {reportConfigurator?.navUrl &&
                    <li key={"093282"} className="treeview">
                        <NavLink to={reportConfigurator?.navUrl} end>
                            <div className="new-icon-Container">
                                <span className="material-icons">rule</span>
                            </div>
                            <span>Report Configurator</span>
                        </NavLink>
                    </li>}
                {reportPreviewUrl &&
                    <li key="report-preview" className="treeview">
                        <NavLink to={reportPreviewUrl}>
                            <div className="new-icon-Container"><span className="material-icons">preview</span></div>
                            <span>Reports Preview</span>
                        </NavLink>
                    </li>}
                    {appversionConfig?.navUrl &&
                    <li key={appversionConfig?.permissionId} className="treeview">
                        <NavLink to={appversionConfig?.navUrl}>
                            <div className="new-icon-Container">
                                <span className="material-icons">bar_chart</span>
                            </div>
                            <span>{appversionConfig?.text}</span>
                        </NavLink>
                    </li>}

            </ul>
        </div>);
};
export default Menu





